import React, { useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Card,
  CardContent,
  Container,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  Grid,
  Checkbox,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../store";
import CountryAutocomplete from "../../components/country-autocomplete";

interface FormValues {
  registeredAddress: {
    country: string;
    address: string;
    zip: string;
  };
  tradingAddress: {
    country: string;
    address: string;
    zip: string;
  };

  tradingAddressSame: string;

  isCompanyStartUp: string;
  sourceBusinessProceeds: boolean;
  sourceInterestDividends: boolean;
  sourceInvestmentSale: boolean;
  sourcePrivateEquity: boolean;
  sourceBusinessLoans: boolean;
  sourceOther: boolean;
  sourceOtherDetails: string;
  willUBOFund: string;
  UBOFundsDetails: string;

  pepStatus: "none" | "atLeastOne";
  pepTransactions: boolean | null;

  sourceOfFundsSelected: boolean;
}

const initialValues: FormValues = {
  registeredAddress: { country: "", address: "", zip: "" },
  tradingAddress: { country: "", address: "", zip: "" },
  tradingAddressSame: "",

  isCompanyStartUp: "",
  sourceBusinessProceeds: false,
  sourceInterestDividends: false,
  sourceInvestmentSale: false,
  sourcePrivateEquity: false,
  sourceBusinessLoans: false,
  sourceOther: false,
  sourceOtherDetails: "",
  willUBOFund: "",
  UBOFundsDetails: "",
  pepStatus: "none",
  pepTransactions: null,
  sourceOfFundsSelected: false,
};

const validationSchema = Yup.object().shape({
  registeredAddress: Yup.object().shape({
    country: Yup.string().required("Required"),
    address: Yup.string().required("Required"),
    zip: Yup.string().required("Required"),
  }),
  tradingAddressSame: Yup.string()
    .oneOf(["yes", "no"])
    .required("Select Yes or No"),
  tradingAddress: Yup.object().when("tradingAddressSame", {
    is: "no",
    then: Yup.object().shape({
      country: Yup.string().required("Required"),
      address: Yup.string().required("Required"),
      zip: Yup.string().required("Required"),
    }),
    otherwise: Yup.object().strip(),
  }),
  isCompanyStartUp: Yup.string().required("Select Yes or No"),
  sourceBusinessProceeds: Yup.boolean(),
  sourceInterestDividends: Yup.boolean(),
  sourceInvestmentSale: Yup.boolean(),
  sourcePrivateEquity: Yup.boolean(),
  sourceBusinessLoans: Yup.boolean(),
  sourceOther: Yup.boolean(),
  sourceOtherDetails: Yup.string().when("sourceOther", {
    is: true,
    then: Yup.string().required("Please specify other source"),
    otherwise: Yup.string(),
  }),
  willUBOFund: Yup.string().required("Select Yes or No"),
  UBOFundsDetails: Yup.string().when("willUBOFund", {
    is: "yes",
    then: Yup.string().required("Please confirm the UBO's source of funds"),
    otherwise: Yup.string(),
  }),
  pepStatus: Yup.string()
    .oneOf(["none", "atLeastOne"])
    .required("Please select a PEP declaration option"),
  pepTransactions: Yup.mixed<boolean>()
    .oneOf([true, false], "Select Yes or No")
    .required("Select Yes or No"),
  sourceOfFundsSelected: Yup.boolean().test(
    "at-least-one-source",
    "Please select at least one Source of Funds",
    function () {
      const {
        sourceBusinessProceeds,
        sourceInterestDividends,
        sourceInvestmentSale,
        sourcePrivateEquity,
        sourceBusinessLoans,
        sourceOther,
      } = this.parent;
      return Boolean(
        sourceBusinessProceeds ||
          sourceInterestDividends ||
          sourceInvestmentSale ||
          sourcePrivateEquity ||
          sourceBusinessLoans ||
          sourceOther
      );
    }
  ),
});

const CompanyInformationForm2 = () => {
  const navigate = useNavigate();
  const newUser = useAppSelector((state) => state.register.newUser);

  if (!newUser) {
    navigate("/signin");
  }

  const onSubmit = (values: FormValues) => {
    const storedData = JSON.parse(localStorage.getItem("form3_json") || "{}");
    const pepNone = values.pepStatus === "none";
    const pepAtLeastOne = values.pepStatus === "atLeastOne";
    const mergedData = { ...storedData, ...values, pepNone, pepAtLeastOne };
    localStorage.setItem("form3_json", JSON.stringify(mergedData));
    navigate("/corp-info-3");
  };

  const {
    handleSubmit,
    getFieldProps,
    values,
    errors,
    touched,
    setFieldValue,
    isSubmitting,
  } = useFormik<FormValues>({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  useEffect(() => {
    if (values.tradingAddressSame === "yes") {
      setFieldValue("tradingAddress", values.registeredAddress);
    } else if (values.tradingAddressSame === "no") {
      setFieldValue("tradingAddress", { country: "", address: "", zip: "" });
    }
  }, [values.tradingAddressSame, values.registeredAddress, setFieldValue]);

  const errorText = (fieldName: keyof FormValues) =>
    touched[fieldName] && errors[fieldName]
      ? (errors[fieldName] as string)
      : "";

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ mt: 4 }}
    >
      <Container maxWidth="md">
        <form onSubmit={handleSubmit}>
          <Card
            elevation={24}
            component={motion.div}
            layoutId="additional-info-card"
          >
            <CardContent>
              <Stack spacing={4}>
                <Typography variant="h5" fontWeight="600">
                  Address, Financial Status &amp; PEP Declaration
                </Typography>

                <Typography variant="h6" mt={2}>
                  Address and Contact Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight="600">
                      Registered Address
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <CountryAutocomplete
                      label="Country"
                      {...getFieldProps("registeredAddress.country")}
                      value={values.registeredAddress.country}
                      kycProgramId={newUser?.kyc_program_id}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Address"
                      fullWidth
                      {...getFieldProps("registeredAddress.address")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="ZIP code"
                      fullWidth
                      {...getFieldProps("registeredAddress.zip")}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend">
                        Is company Trading Address same as Registered Address?
                      </FormLabel>
                      <RadioGroup
                        row
                        name="tradingAddressSame"
                        value={values.tradingAddressSame}
                        onChange={(_, v) =>
                          setFieldValue("tradingAddressSame", v)
                        }
                      >
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  {values.tradingAddressSame !== "" && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" fontWeight="600">
                          Trading Address
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <CountryAutocomplete
                          label="Country"
                          {...getFieldProps("tradingAddress.country")}
                          value={
                            values.tradingAddressSame === "yes"
                              ? values.registeredAddress.country
                              : values.tradingAddress.country
                          }
                          disabled={values.tradingAddressSame === "yes"}
                          kycProgramId={newUser?.kyc_program_id}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="Address"
                          fullWidth
                          {...getFieldProps("tradingAddress.address")}
                          value={
                            values.tradingAddressSame === "yes"
                              ? values.registeredAddress.address
                              : values.tradingAddress.address
                          }
                          disabled={values.tradingAddressSame === "yes"}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          label="ZIP code"
                          fullWidth
                          {...getFieldProps("tradingAddress.zip")}
                          value={
                            values.tradingAddressSame === "yes"
                              ? values.registeredAddress.zip
                              : values.tradingAddress.zip
                          }
                          disabled={values.tradingAddressSame === "yes"}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>

                <Typography variant="h6" mt={2}>
                  Financial Status
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend">
                        Is the company a start-up?
                      </FormLabel>
                      <RadioGroup row {...getFieldProps("isCompanyStartUp")}>
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>
                      {errorText("isCompanyStartUp") && (
                        <Typography variant="caption" color="error">
                          {errorText("isCompanyStartUp")}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography>
                      Please advise the <strong>Source of Funds</strong> to use
                      to fund your account:
                    </Typography>
                    <Grid container spacing={2} mt={1}>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.sourceBusinessProceeds}
                              onChange={(e) =>
                                setFieldValue(
                                  "sourceBusinessProceeds",
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label="Business Proceeds"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.sourceInterestDividends}
                              onChange={(e) =>
                                setFieldValue(
                                  "sourceInterestDividends",
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label="Interest/Dividends"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.sourceInvestmentSale}
                              onChange={(e) =>
                                setFieldValue(
                                  "sourceInvestmentSale",
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label="Investment/Sale of Assets"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.sourcePrivateEquity}
                              onChange={(e) =>
                                setFieldValue(
                                  "sourcePrivateEquity",
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label="Private Equity/Venture Capital"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.sourceBusinessLoans}
                              onChange={(e) =>
                                setFieldValue(
                                  "sourceBusinessLoans",
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label="Business Loans Funds"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.sourceOther}
                              onChange={(e) =>
                                setFieldValue("sourceOther", e.target.checked)
                              }
                            />
                          }
                          label="Other (please specify)"
                        />
                      </Grid>
                      {errors.sourceOfFundsSelected && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 1 }}
                        >
                          {errors.sourceOfFundsSelected as string}
                        </Typography>
                      )}
                      {values.sourceOther && (
                        <Grid item xs={12}>
                          <TextField
                            label="Please specify other source"
                            {...getFieldProps("sourceOtherDetails")}
                            error={!!errorText("sourceOtherDetails")}
                            helperText={errorText("sourceOtherDetails")}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend">
                        Will any of the UBOs (directly or indirectly) fund the
                        company?
                      </FormLabel>
                      <RadioGroup row {...getFieldProps("willUBOFund")}>
                        <FormControlLabel
                          value="yes"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="no"
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>
                      {errorText("willUBOFund") && (
                        <Typography variant="caption" color="error">
                          {errorText("willUBOFund")}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  {values.willUBOFund === "yes" && (
                    <Grid item xs={12}>
                      <TextField
                        label="Please confirm the UBO's source of funds"
                        multiline
                        minRows={2}
                        fullWidth
                        {...getFieldProps("UBOFundsDetails")}
                        error={!!errorText("UBOFundsDetails")}
                        helperText={errorText("UBOFundsDetails")}
                      />
                    </Grid>
                  )}
                </Grid>

                <Typography variant="h6" mt={2}>
                  Politically Exposed Person (PEP) Declaration
                </Typography>
                <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                  A PeP refers to an individual who holds a prominent public
                  position or has held such a position in the recent past. These
                  positions typically include high-ranking government officials,
                  politicians, senior executives in state-owned enterprises,
                  judicial officials, and military personnel.
                </Typography>
                <Typography>
                  Having read and understood the definition of a PEP as per
                  above, I confirm that:
                </Typography>
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    Please select one option:
                  </FormLabel>
                  <RadioGroup row {...getFieldProps("pepStatus")}>
                    <FormControlLabel
                      value="none"
                      control={<Radio />}
                      label="None of our UBOs/Directors/Shareholders are PEPs"
                    />
                    <FormControlLabel
                      value="atLeastOne"
                      control={<Radio />}
                      label="At least one of our UBOs/Directors/Shareholders is a PEP"
                    />
                  </RadioGroup>
                </FormControl>
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    The company engages in transactions involving publicly
                    exposed persons (PEPs)
                  </FormLabel>
                  <RadioGroup
                    row
                    name="pepTransactions"
                    value={
                      values.pepTransactions === null
                        ? ""
                        : String(values.pepTransactions)
                    }
                    onChange={(_, v) =>
                      setFieldValue("pepTransactions", v === "true")
                    }
                  >
                    <FormControlLabel
                      value="true"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="false"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                  {touched.pepTransactions && errors.pepTransactions && (
                    <Typography variant="caption" color="error">
                      {errors.pepTransactions}
                    </Typography>
                  )}
                </FormControl>

                <Stack spacing={2} mt={3}>
                  <LoadingButton
                    fullWidth
                    loading={isSubmitting}
                    variant="contained"
                    type="submit"
                  >
                    Save &amp; Continue
                  </LoadingButton>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </form>
      </Container>
    </Box>
  );
};

export default CompanyInformationForm2;
